/**
 * Client-side clamp forecast roll-forward after a single cell or PO change.
 * Re-applies planned forecast arrivals onto the cached MRP base rows — no
 * runForecast() round trip.
 */
import { applyClampForecastGhostArrivals } from "@integrator-src/mrp/clamp-forecast-ghost-arrivals";
import { applyClampForecastPlannedArrivals } from "@integrator-src/mrp/clamp-forecast-planned-arrivals";

export interface ClampForecastPlannedArrivalCellInput {
  id: number;
  category_code: string;
  plan_month: string;
  forecast_qty: number;
  expected_arrival_date: string | null;
}

export interface ClampForecastPoLinkInput {
  forecast_id: number;
  expected_arrival: string | null;
  sku: string;
  quantity_ordered: number;
}

export interface ClampForecastGhostInput {
  category_code: string;
  plan_month: string;
  ghost_qty: number;
  expected_arrival_date?: string | null;
}

export interface ClampForecastDerivedDemandRowInput {
  category_code: string;
  derived_demand_sku: string;
  plan_month: string;
  location_code: string;
  rolled_qty_pre_order: number;
  starting_qty: number;
  arrivals: number;
  demand: number;
  next_component_order_date: string | null;
  pipeline_breakdown?: {
    sku: string;
    rolled_qty_pre_order: number;
    starting_qty: number;
    arrivals: number;
    demand: number;
  }[];
}

export interface ClampForecastCategoryInput {
  code: string;
  is_kit: boolean;
  fg_sku: string | null;
}

export interface ClampForecastDerivedDemandSkuInput {
  category_code: string;
  sku: string;
}

export interface ClampForecastKitItemInput {
  category_code: string;
  component_sku: string;
  quantity_per_kit: number;
}

function indexPoLinksByForecastId(
  links: ClampForecastPoLinkInput[],
): Map<number, ClampForecastPoLinkInput[]> {
  const map = new Map<number, ClampForecastPoLinkInput[]>();
  for (const link of links) {
    const list = map.get(link.forecast_id) ?? [];
    list.push(link);
    map.set(link.forecast_id, list);
  }
  return map;
}

function buildKitItemsByCategory(
  kitItems: ClampForecastKitItemInput[],
  categoryCode: string,
): Map<string, { component_sku: string; quantity_per_kit: number }[]> {
  const rows = kitItems
    .filter((item) => item.category_code === categoryCode)
    .map((item) => ({
      component_sku: item.component_sku,
      quantity_per_kit: item.quantity_per_kit,
    }));
  return new Map([[categoryCode, rows]]);
}

/**
 * Recompute derived-demand middle-line rows for one category by re-applying
 * clamp forecast planned arrivals onto the MRP base (pre-arrival) rows.
 */
export function recomputeCategoryClampForecastRollForward<
  Row extends ClampForecastDerivedDemandRowInput,
>(
  categoryCode: string,
  category: ClampForecastCategoryInput,
  derivedDemandBase: Row[],
  cells: ClampForecastPlannedArrivalCellInput[],
  poLinks: ClampForecastPoLinkInput[],
  rawPurchasedSku: string | null,
  kitItems: ClampForecastKitItemInput[] = [],
  ghosts: ClampForecastGhostInput[] = [],
): Row[] {
  if (category.is_kit) return [];
  const categoryBase = derivedDemandBase.filter((row) => row.category_code === categoryCode);
  if (categoryBase.length === 0) return [];

  const categoryCells = cells.filter((cell) => cell.category_code === categoryCode);
  const categoryGhosts = ghosts.filter((ghost) => ghost.category_code === categoryCode);
  const rawSkuByCategory = new Map<string, string | null>([[categoryCode, rawPurchasedSku]]);

  const withPlannedArrivals = applyClampForecastPlannedArrivals(
    categoryCells,
    categoryBase,
    rawSkuByCategory,
    indexPoLinksByForecastId(poLinks),
    {
      categoryByCode: new Map([[categoryCode, category]]),
      kitItemsByCategory: buildKitItemsByCategory(kitItems, categoryCode),
    },
  ) as Row[];

  return applyClampForecastGhostArrivals(categoryGhosts, withPlannedArrivals, rawSkuByCategory, {
    cells: cells.map((cell) => ({
      id: cell.id,
      category_code: cell.category_code,
      plan_month: cell.plan_month,
      expected_arrival_date: cell.expected_arrival_date,
    })),
    poLinksByForecastId: indexPoLinksByForecastId(poLinks),
  }) as Row[];
}
